import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSignOutAlt,
  faUser,
  faCog,
  faPlus,
  faFileContract,
  faWrench
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import { contractService } from '../../../services/contractService';

// Default URProperty logo URL
const DEFAULT_LOGO_URL = '/urproperty-logo.png';

const PMSNavbarTop = () => {
  const { userRole, userCompany, logout } = useAuth();
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState('');
  const [logoEnabled, setLogoEnabled] = useState(false);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      if (userCompany && userRole === 'manager') {
        try {
          const settings = await contractService.getBrokerageSettings(userCompany);
          setLogoEnabled(settings.logoEnabled || false);
          // Use company logo if logo is enabled and URL exists, otherwise keep empty for fallback
          setCompanyLogo((settings.logoEnabled && settings.logoUrl) ? settings.logoUrl : '');
        } catch (error) {
          console.error('Error fetching company logo:', error);
          setCompanyLogo('');
          setLogoEnabled(false);
        }
      }
    };

    fetchCompanyLogo();
  }, [userCompany, userRole]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getDashboardLink = () => {
    switch (userRole) {
      case 'admin':
        return '/admin/dashboard';
      case 'manager':
        return '/manager/dashboard';
      default:
        return '/dashboard';
    }
  };

  return (
    <Navbar 
      bg="white" 
      expand="lg" 
      className="shadow-sm border-bottom mb-4"
      style={{ minHeight: '70px' }}
    >
      <div className="container-fluid">
        {/* Logo */}
        <Navbar.Brand as={Link} to={getDashboardLink()} className="d-flex align-items-center">
          <img 
            src={(logoEnabled && companyLogo) ? companyLogo : DEFAULT_LOGO_URL} 
            alt={(logoEnabled && companyLogo) ? "Company Logo" : "URProperty"} 
            style={{ 
              height: '50px', 
              width: 'auto', 
              maxWidth: '200px',
              objectFit: 'contain'
            }}
            className="me-2"
          />
        </Navbar.Brand>

        {/* Navigation Buttons */}
        <Navbar.Toggle aria-controls="pms-navbar-nav" />
        <Navbar.Collapse id="pms-navbar-nav">
          <Nav className="me-auto">
            {userRole === 'manager' && (
              <div className="d-flex gap-2 align-items-center">
                <Button as={Link} to="/manager/property-groups/new" variant="primary" size="sm">
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Property Group
                </Button>
                <Button as={Link} to="/manager/tenants" variant="success" size="sm">
                  <FontAwesomeIcon icon={faUser} className="me-1" />
                  Manage Tenants
                </Button>
                <Button as={Link} to="/manager/contracts" variant="info" size="sm">
                  <FontAwesomeIcon icon={faFileContract} className="me-1" />
                  Manage Contracts
                </Button>
                <Button as={Link} to="/manager/maintenance" variant="warning" size="sm">
                  <FontAwesomeIcon icon={faWrench} className="me-1" />
                  Maintenance
                </Button>
              </div>
            )}
            {userRole === 'admin' && (
              <>
                <Nav.Link as={Link} to="/admin/companies">Companies</Nav.Link>
                <Nav.Link as={Link} to="/admin/users">Users</Nav.Link>
              </>
            )}
          </Nav>

          {/* Right Side Actions */}
          <Nav className="ms-auto">
            {userRole === 'manager' && (
              <Button as={Link} to="/manager/settings/admin-fees" variant="outline-secondary" size="sm" className="me-2">
                <FontAwesomeIcon icon={faCog} className="me-1" />
                Settings
              </Button>
            )}
            <Button variant="outline-danger" size="sm" onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
              Logout
            </Button>
          </Nav>
        </Navbar.Collapse>
      </div>
    </Navbar>
  );
};

export default PMSNavbarTop; 